import { useId, type CSSProperties, type InputHTMLAttributes, type ReactNode } from "react";

/**
 * TheCore Studio — Text input with optional label + leading icon.
 * The outer field is a styled wrapper; the native `<input>` is its
 * only child and inherits the focus styling via the wrapper's
 * `:focus-within` heuristic. (We use JS focus/blur on the wrapper
 * to drive the ring, matching the reference implementation.)
 */
export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "style"> {
  label?: string;
  hint?: string;
  iconLeft?: ReactNode;
  /** Rendered after the input inside the field shell (e.g. a reveal toggle). */
  trailing?: ReactNode;
  invalid?: boolean;
  style?: CSSProperties;
  containerStyle?: CSSProperties;
}

export function Input({
  label,
  hint,
  iconLeft = null,
  trailing = null,
  invalid = false,
  style = {},
  containerStyle = {},
  id,
  ...rest
}: InputProps) {
  const reactId = useId();
  const inputId = id ?? (label ? label.toLowerCase().replace(/\s+/g, "-") : reactId);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        // Fields are routinely placed in a `display: flex` row (`flex: 1`).
        // Without this, the wrapper's automatic minimum size is the input's
        // intrinsic width (~20 characters), so the row could not shrink below
        // it and pushed its siblings past the edge of the frame.
        minWidth: 0,
        ...containerStyle,
      }}
    >
      {label && (
        <label
          htmlFor={inputId}
          style={{ font: "var(--text-label)", color: "var(--text-body)" }}
        >
          {label}
        </label>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 46,
          padding: "0 14px",
          background: "var(--surface-card)",
          border: `1px solid ${invalid ? "var(--rust-500)" : "var(--border-default)"}`,
          borderRadius: "var(--radius-md)",
          transition:
            "border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = invalid ? "var(--rust-500)" : "var(--focus-ring)";
          e.currentTarget.style.boxShadow = `0 0 0 3px ${
            invalid ? "var(--rust-100)" : "var(--focus-ring-soft)"
          }`;
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = invalid ? "var(--rust-500)" : "var(--border-default)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        {iconLeft && (
          <span style={{ display: "inline-flex", color: "var(--text-faint)" }}>{iconLeft}</span>
        )}
        <input
          id={inputId}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            font: "var(--text-body-base)",
            color: "var(--text-strong)",
            ...style,
          }}
          {...rest}
        />
        {trailing}
      </div>
      {hint && (
        <span
          style={{
            font: "var(--fw-regular) var(--fs-xs)/1.4 var(--font-body)",
            color: invalid ? "var(--rust-700)" : "var(--text-faint)",
          }}
        >
          {hint}
        </span>
      )}
    </div>
  );
}
